import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { IGroups, ISites } from 'utils/models/groups';
import { User } from 'utils/models/user';
import Button from '../button';
import ApiService from 'utils/services/ApiService';
import ConfirmationButtons from '../button/ConfirmationButtons';
import DeleteConfirmationDialog from '../dialog/DeleteGroup';
import Tooltip from '@mui/material/Tooltip';
import Sites from './Sites';
import { TextField } from '@mui/material';
import { Draggable as DraggableBase, Droppable as DroppableBase } from 'react-beautiful-dnd';
import { getTestResults, saveDeletionSyncCache, saveSyncCache, saveTestResults } from 'utils/services/idbService';
import useCheckUserIsOnline from 'hooks/useCheckUserOnline';
import type { DroppableProps, DraggableProps } from 'react-beautiful-dnd';
import { useDispatch } from 'react-redux';
import { deleteGroup, updateGroup } from 'utils/redux/reducers/groupsReducer';

interface GroupProps {
	addStyle?: string;
	index?: number;
	userData: User | undefined;
	group: IGroups;
	allGroups: IGroups[];
	showDeleteGroup: number;
	setShowDeleteGroup: React.Dispatch<React.SetStateAction<number>>;
	sendPopUpMessage: (type: string, message: string) => void;
	newSite: any;
	setNewSite: React.Dispatch<React.SetStateAction<any>>;
	handleAddNewSite: () => void;
	viewedUserState: number;
}

const Droppable = DroppableBase as React.ComponentClass<DroppableProps>;
const Draggable = DraggableBase as React.ComponentClass<DraggableProps>;

const Groups: React.FC<GroupProps> = ({
	addStyle = '',
	index,
	userData,
	group,
	allGroups,
	showDeleteGroup,
	setShowDeleteGroup,
	sendPopUpMessage,
	newSite,
	setNewSite,
	handleAddNewSite,
	viewedUserState,
}) => {
	const userLevel = userData?.userPermissionVOs[0].level;
	const userId = userData?.id;
	const { isOnline } = useCheckUserIsOnline();
	const dispatch = useDispatch();

	const [siteToEdit, setSiteToEdit] = useState<ISites>({
		name: '',
		id: 0,
		status: 'true',
		applicationVO: {
			id: 2,
			name: 'Activated Sludge',
			industry: 'Waste Water',
			status: 'Active',
		},
		sortOrder: 999,
		addressVO: {},
		siteIndustryVO: [
			{
				id: 13,
				name: 'Waste Water',
				custom: true,
			},
		],
	});
	const [sites, setSites] = useState<any>([]);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [showAddNewSite, setShowAddNewSite] = useState(false);
	const [showConfirmationLoading, setShowConfirmationLoading] =
		useState(false);

	const [editGroupName, setEditGroupName] = useState(group.name);
	const [showEditGroup, setShowEditGroup] = useState(false);

	// Add useMemo for sites
	const sitesMemo = useMemo(() => group.sites || [], [group.sites]);

	// Update the updateSites function
	const updateSites = useCallback((group: IGroups) => {
		if (group.sites) {
			setSites(group.sites);
		}
	}, []);

	// Update useEffect to use the callback
	useEffect(() => {
		updateSites(group);
	}, [group, updateSites]);

	// Function to handle saving the edited group name
	const handleEditGroupName = async () => {
		setShowConfirmationLoading(true);
		try {
			if (!isOnline) {
				await saveSyncCache({
					id: group.id,
					name: editGroupName,
					action: 'UPDATE'
				}, "groupSyncCache");

				 const groupsCache = await getTestResults(
        `groupsInfo`,
        localStorage.getItem('currentUser') ?? 'unknown'
      );

      const updatedGroupsCache = groupsCache.map((cachedGroup: IGroups) => {
        if (cachedGroup.id === group.id) {
          return {
            ...cachedGroup,
            name: editGroupName
          };
        }
        return cachedGroup;
      });

      await saveTestResults(
        `groupsInfo`,
        updatedGroupsCache,
        localStorage.getItem('currentUser') ?? 'unknown'
      );
				// Update Redux state
				dispatch(updateGroup({ id: group.id, name: editGroupName, userId: group.userId ?? 0 }));
				setShowEditGroup(false);
				return;
			}

			const result = await ApiService.editGroup({
				id: group.id,
				name: editGroupName,
			});

			if (result.status === 200) {
				// Update Redux state
				dispatch(updateGroup({ id: group.id, name: editGroupName, userId: group.userId ?? 0 }));
				setShowEditGroup(false);
				sendPopUpMessage('Success', 'Group name updated successfully.');
			}
		} catch (error) {
			console.error(error);
			sendPopUpMessage('Error', 'Failed to update group name.');
		} finally {
			setShowConfirmationLoading(false);
		}
	};

	// Function to start editing the group name
	const handleStartEditGroup = () => {
		setEditGroupName(group.name);
		setShowEditGroup(true);
	};

	// Function to cancel editing the group name
	const handleCancelEditGroup = () => {
		setEditGroupName(group.name); // Reset the name back to the original
		setShowEditGroup(false);
	};

	const moveSite = (from: number, to: number) => {
		if (sites) {
			const updatedSites = [...sites];
			const [movedSite] = updatedSites.splice(from, 1);
			updatedSites.splice(to, 0, movedSite);
			setSites(updatedSites);
		}
	};

	const allowUserToEditSite = (siteOwnerIds: number[]) => {
		if (userLevel !== 3) return true;
		if (userLevel === 3 && userId && siteOwnerIds.includes(userId))
			return true;
		return false;
	};

	const handleDeleteGroup = async () => {
		const siteIDsToRemove: number[] = [];
		setShowDeleteGroup(0);
		setOpenDeleteDialog(false);
		
		try {
			if (!isOnline) {
				await saveDeletionSyncCache({
					deletedGroups: [group.id],
				}, "deletionSyncCache");
				
				const groupsCache = await getTestResults(
					`groupsInfo`,
					localStorage.getItem('currentUser') ?? 'unknown'
				  );
			
				  const updatedGroupsCache = groupsCache.filter(
					(cachedGroup: IGroups) => cachedGroup.id !== group.id
				  );
			
				  await saveTestResults(
					`groupsInfo`,
					updatedGroupsCache,
					localStorage.getItem('currentUser') ?? 'unknown'
				  );
				// Update Redux state
				dispatch(deleteGroup(group.id));
				
				// 2. Handle associated sites in offline mode
				if (group?.sites && group.sites.length > 0) {
					// Save site deletions to sync cache
					for (const site of group.sites) {
						await saveDeletionSyncCache({
							deletedSites: [site.id],
						}, "deletionSyncCache");
						siteIDsToRemove.push(site.id);
					}

					// Update local cache
					try {
						const cachedData = await getTestResults(
							`allSites`,
							localStorage.getItem('currentUser') ?? 'unknown'
						);

						const updatedCache = cachedData.filter(
							(site: any) => !siteIDsToRemove.includes(site.id)
						);

						await saveTestResults(
							`allSites`,
							updatedCache,
							localStorage.getItem('currentUser') ?? 'unknown'
						);
					} catch (cacheError) {
						console.error('Error updating local cache:', cacheError);
						sendPopUpMessage('Warning', 'Local cache update failed, data may be inconsistent until next sync');
					}
				}

				// 3. Update UI optimistically
				sendPopUpMessage('Success', 'Group marked for deletion. Changes will sync when online.');
				return;
			}

			// Online mode deletion
			const result = await ApiService.deleteAGroup(group.id, userId);
			
			if (result.status === 200 || result.status === 204) {
				dispatch(deleteGroup(group.id));
				if (group?.sites && group.sites.length > 0) {
					// Handle site cache cleanup
					try {
						const cachedData = await getTestResults(
							`allSites`,
							localStorage.getItem('currentUser') ?? 'unknown'
						);

						const updatedCache = cachedData.filter(
							(site: any) => !group?.sites?.map(s => s.id).includes(site.id)
						);

						await saveTestResults(
							`allSites`,
							updatedCache,
							localStorage.getItem('currentUser') ?? 'unknown'
						);
					} catch (cacheError) {
						console.error('Error updating cache after online deletion:', cacheError);
					}
				}
				sendPopUpMessage('Success', 'Group deleted successfully.');
			} else {
				throw new Error('Server returned unexpected status');
			}
		} catch (error) {
			console.error('Delete group error:', error);
			sendPopUpMessage('Error', 'Failed to delete group. Please try again.');
			setShowConfirmationLoading(false);
		}
	};

	const handleNewSiteName = (e: React.ChangeEvent<HTMLInputElement>) => {
		setNewSite({
			...newSite,
			name: e.target.value,
			groupId: group.id,
		});
	};

	const handleShowAddNewSite = () => {
		setSiteToEdit({
			...siteToEdit,
			name: '',
		});
		setNewSite({
			...newSite,
			name: '',
		});
		setShowAddNewSite(true);
	};

	const addNewSite = async () => {
		setShowConfirmationLoading(true);
		handleAddNewSite();
		setShowAddNewSite(false);
		setShowConfirmationLoading(false);
	};

	const handleShowDeleteGroup = () => {
		setOpenDeleteDialog(true);
		setShowDeleteGroup(group.id);
	};

	const handleCancelNewSite = () => {
		setNewSite({
			...newSite,
			name: '',
		});
		setShowAddNewSite(false);
	};

	return (
		<div className="flex mb-2" id="one-group-and-sites">
			<div className={` bg-white flex py-2 w-[100%]  ${addStyle}`}>
				<div className="flex-1 py-2 text-left mb-2 group">
					<div className="flex">
						<div className="flex">
							{showEditGroup ? (
								<TextField
									size="small"
									value={editGroupName}
									onChange={(e) =>
										setEditGroupName(e.target.value)
									}
									style={{
										width: 298,
										marginTop: 0,
										marginRight: 10,
									}}
									sx={{
										'& .MuiInputBase-root': {
											fontFamily: 'Jost, sans-serif',
										},
									}}
									label=""
								/>
							) : (
								<div className="!font-jost !w-[298px] !h-[40px] rounded-[4px] border-[1px] px-[12px] py-[12px] flex flex-row items-center">
									{group.name === 'Ungrouped'
										? 'TEST+ANALYZE SITES'
										: group.name}
								</div>
							)}
						</div>
						<div className="flex items-center">
							{showEditGroup ? (
								<ConfirmationButtons
									handleConfirm={handleEditGroupName}
									handleCancel={handleCancelEditGroup}
									disabled={
										 showConfirmationLoading
									}
								/>
							) : (
								<>
									{!showAddNewSite && (
										<Tooltip title="Edit group">
											<button
												className={`
                          !w-[40px] h-[40px] p-[8px] pl-[11px] border-transparent border-[1px] 
                          hover:shadow-sm hover:border-[#ECEDEF] hover:border-[1px] 
                          focus:shadow-sm focus:border-[#ECEDEF] focus:border-[1px]
                           ${showConfirmationLoading && 'cursor-progress'}
                          `}
												onClick={handleStartEditGroup}
												disabled={
													
													showConfirmationLoading
												}
												style={{
													marginLeft: '8px',
												}}
											>
												<img
													src="/assets/icons/Edit.svg"
													alt="edit icon"
												/>
											</button>
										</Tooltip>
									)}
									{!showAddNewSite && (
										<Tooltip title="Delete group">
											<button
												className={`
                !w-[40px] h-[40px] p-[8px] pl-[11px] border-transparent border-[1px] 
                hover:shadow-sm hover:border-[#ECEDEF] hover:border-[1px] 
                focus:shadow-sm focus:border-[#ECEDEF] focus:border-[1px]
                 ${showConfirmationLoading && 'cursor-progress'}
                `}
												type="button"
												onClick={handleShowDeleteGroup}
											>
												<img
													src="/assets/icons/Trash.svg"
													alt="Delete Icon"
													className="w-5 h-5 md:block hidden"
												/>
											</button>
										</Tooltip>
									)}
								</>
							)}
						</div>
					</div>
				</div>

				<div className="flex-1 py-2 text-left mb-2 sites">
					<Droppable droppableId={`${group.id}`}>
						{(provided: any) => (
							<div
								{...provided.droppableProps}
								ref={provided.innerRef}
							>
								{sitesMemo.length > 0 ? (
									sitesMemo.map((site: ISites, idx: number) => (
										<div key={`site-${site.id}`}>
											<Draggable
												draggableId={`${site.id}`}
												index={idx}
												key={site.id}
												// isDragDisabled={!isOnline}
											>
												{(provided) => (
													<div
														{...provided.dragHandleProps}
														{...provided.draggableProps}
														ref={provided.innerRef}
													>
														{' '}
														{/* Ensure the key is unique and stable */}
														<Sites
															key={site.id}
															site={site}
															index={idx}
															groupId={group.id}
															moveSite={moveSite}
															allowUserToEditSite={
																allowUserToEditSite
															}
															setShowAddNewSite={
																setShowAddNewSite
															}
															sendPopUpMessage={
																sendPopUpMessage
															}
														/>
													</div>
												)}
											</Draggable>
										</div>
									))
								) : (
									<div
										style={{
											padding: 0,
											textAlign: 'center',
										}}
									></div>
								)}
								{provided.placeholder}
								{showAddNewSite && (
									<div className="!w-[400px] !h-[40px] flex items-center justify-between mb-4">
										<TextField
											size="small"
											value={newSite.name}
											style={{
												width: 320,
												marginTop: 0,
												marginRight: 10,
											}}
											
											onChange={handleNewSiteName}
											// error={errors.groupName !== ''}
											// helperText={errors.groupName}
											label="Site Name"
										/>
										<div className="w-[100px]">
											{showAddNewSite && (
												<ConfirmationButtons
													handleConfirm={addNewSite}
													handleCancel={
														handleCancelNewSite
													}
													disabled={
														showConfirmationLoading
													}
												/>
											)}
										</div>
									</div>
								)}
								<Button
									onClick={handleShowAddNewSite}
									addStyle={`!h-[48px] !font-bold ${
										viewedUserState === 0
											? '!text-[#0092C8]'
											: '!text-[#e6e6e6]'
									} !text-[14px] add-new-site !font-bold  
										${showAddNewSite && '!mt-[10px]'}
										${group.sites?.length === 0 && '!mt-[-4px]'}
									`}
									disabled={viewedUserState !== 0}
								>
									+ Add new Site
								</Button>
							</div>
						)}
					</Droppable>
				</div>
			</div>
			<DeleteConfirmationDialog
				open={openDeleteDialog}
				onClose={() => setOpenDeleteDialog(false)}
				onConfirm={handleDeleteGroup}
				groupName={group.name}
			/>
		</div>
	);
};

export default Groups;